// Thin wrapper around @sentry/react so the rest of the app can call a tiny,
// stable API and doesn't have to check whether Sentry is actually on.
//
// The wrapper is a no-op when VITE_SENTRY_DSN is missing, which is the
// case in tests, local dev, and any build where the env var hasn't been set.
// That lets us commit the wiring everywhere without forcing every contributor
// to sign up for a Sentry account.

import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
  createBrowserRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';

let active = false;

function readEnv(key) {
  try {
    return import.meta.env?.[key];
  } catch {
    return undefined;
  }
}

function shouldEnable() {
  const dsn = readEnv('VITE_SENTRY_DSN');
  if (!dsn) return false;
  // Allow opt-in in dev with VITE_SENTRY_FORCE=1 for smoke tests. Otherwise prod only.
  if (readEnv('PROD')) return true;
  if (readEnv('VITE_SENTRY_FORCE')) return true;
  return false;
}

/** Initialise Sentry. Safe to call multiple times; only the first call binds. */
export function initSentry() {
  if (active) return;
  if (!shouldEnable()) return;

  Sentry.init({
    dsn: readEnv('VITE_SENTRY_DSN'),
    environment: readEnv('VITE_SENTRY_ENV') || (readEnv('PROD') ? 'production' : 'development'),
    release: readEnv('VITE_APP_VERSION') || undefined,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
  });

  active = true;
}

/** True when Sentry has been initialised and is sending events. */
export function isSentryActive() {
  return active;
}

/** Send a caught error to Sentry with optional structured context. */
export function reportToSentry(error, context) {
  if (!active || !error) return;
  if (context) {
    Sentry.captureException(error, { extra: context });
  } else {
    Sentry.captureException(error);
  }
}

/** Tag future events with the signed-in user. */
export function setSentryUser(user) {
  if (!active) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: user.id || user.uid, email: user.email });
}

/** Clear the user context (on logout). */
export function clearSentryUser() {
  if (!active) return;
  Sentry.setUser(null);
}

/** Tag future events with the active household. */
export function setSentryHousehold(household) {
  if (!active) return;
  if (!household || !household.id) {
    Sentry.setTag('householdId', undefined);
    Sentry.setContext('household', null);
    return;
  }
  Sentry.setTag('householdId', household.id);
  if (household.name) {
    Sentry.setContext('household', { id: household.id, name: household.name });
  }
}

/**
 * Returns `createBrowserRouter` (wrapped by Sentry when active, vanilla
 * otherwise). Keeps Router.jsx free of init-order concerns.
 */
export function getBrowserRouterFactory() {
  if (active) return Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
  return createBrowserRouter;
}
